import React, { useEffect, useContext } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Box, CircularProgress } from "@mui/material"
import i18n from "i18n"

import { useAuthContext } from "context/auth/authContext"
import {
  useOrganizationInvoices,
  useProjectInvoices,
} from "./hooks/useInvoices"
import InvoicesHeader from "./Dashboard/Header"
import InvoiceDataGrid from "./Dashboard/DataGrid"
import EmptyState from "common/EmptyState"
import ProjectContext from "context/projects/projectContext"

const Invoices = () => {
  const { projectId } = useParams()
  const navigate = useNavigate()

  const { user } = useAuthContext()
  const organizationId = user?.["custom:organizationId"]
  const isProjectRoute = !!projectId
  const {
    projects = [],
    getProjects,
    loadingProjects,
  } = useContext(ProjectContext)

  useEffect(() => {
    if (
      user &&
      projects.length === 0 &&
      typeof getProjects === "function" &&
      !loadingProjects
    ) {
      getProjects()
    }
  }, [user, projects, getProjects, loadingProjects])

  const projectInvoicesQuery = useProjectInvoices(
    organizationId,
    projectId,
    isProjectRoute && !!organizationId,
  )
  const organizationInvoicesQuery = useOrganizationInvoices(
    organizationId,
    !isProjectRoute && !!organizationId,
  )

  const {
    data: invoices,
    isLoading,
    error,
  } = isProjectRoute ? projectInvoicesQuery : organizationInvoicesQuery

  useEffect(() => {
    if (error) {
      console.error("Error fetching invoices:", error)
    }
  }, [error])

  if (!user) return null

  const handleAddNewInvoice = () => {
    let url = `/invoice/new`
    if (projectId) {
      url += `/${projectId}`
    }
    navigate(url)
  }

  return (
    <Box>
      <InvoicesHeader onAddNewInvoice={handleAddNewInvoice} />

      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "120px",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {error && <div>{i18n.t("invoices.errorLoadingInvoices")}</div>}
      {invoices && Array.isArray(invoices) && invoices.length === 0 && (
        <EmptyState
          title={i18n.t("invoices.noInvoicesYet")}
          buttonText={i18n.t("invoices.newInvoice")}
          onButtonClick={handleAddNewInvoice}
        />
      )}
      {invoices && Array.isArray(invoices) && invoices.length > 0 && (
        <InvoiceDataGrid
          invoices={invoices}
          projects={projects}
          projectId={projectId}
        />
      )}
    </Box>
  )
}

export default Invoices
